import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../../ui/dialog";
import { api, errMsg } from "../../../lib/api";
import UserPicker from "./UserPicker";
import LinkResult from "./LinkResult";

const field = "w-full h-10 px-3 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-900 outline-none focus:border-sky-400 focus:bg-white";
const EMPTY = { credits: 50, inr: "", phone: "", note: "" };

const LinkForm = ({ onCreated }) => {
  const [who, setWho] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [unit, setUnit] = useState(0);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.creditsPricing().then((r) => setUnit(r.unit_price_inr)).catch(() => {}); }, []);
  useEffect(() => { setF((x) => ({ ...x, phone: who?.phone || "" })); }, [who]);
  const set = (u) => setF((x) => ({ ...x, ...u }));
  const suggested = Math.max(1, Math.ceil(Number(f.credits || 0) * unit));
  const price = unit ? (f.inr === "" ? suggested : Number(f.inr)).toLocaleString("en-IN") : "…";

  const submit = async (e) => {
    e.preventDefault();
    if (!who) return toast.error("Pick the merchant first");
    setBusy(true);
    try {
      onCreated(await api.adminCreatePaymentLink({ user_id: who.id, credits: Number(f.credits), amount_inr: f.inr === "" ? undefined : Number(f.inr),
        phone: f.phone || undefined, note: f.note || undefined }));
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <UserPicker value={who} onPick={setWho} />
      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs text-slate-500 space-y-1.5 block">Credits
          <input type="number" min={1} max={100000} step={1} required value={f.credits} onChange={(e) => set({ credits: e.target.value })} data-testid="payment-link-credits" className={`${field} font-mono`} /></label>
        <label className="text-xs text-slate-500 space-y-1.5 block">Amount to charge (₹)
          <input type="number" min={1} step="0.01" value={f.inr} placeholder={unit ? String(suggested) : "…"} onChange={(e) => set({ inr: e.target.value })} data-testid="payment-link-inr" className={`${field} font-mono`} /></label>
      </div>
      <label className="text-xs text-slate-500 space-y-1.5 block">Mobile for SMS (optional)
        <input type="tel" value={f.phone} onChange={(e) => set({ phone: e.target.value })} placeholder="98765 43210" data-testid="payment-link-phone" className={field} /></label>
      <input value={f.note} onChange={(e) => set({ note: e.target.value })} maxLength={200} placeholder="Note (optional)" data-testid="payment-link-note" className={field} />
      <p className="text-[11px] text-slate-400">Razorpay emails the link{f.phone ? " and texts it" : ""} to the merchant. Credits are added automatically the moment they pay.</p>
      <button type="submit" disabled={busy || !who || !unit || Number(f.credits) < 1} data-testid="payment-link-create"
        className="w-full h-11 rounded-full bg-slate-900 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-slate-700 disabled:opacity-50 transition-colors">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Link2 size={15} />} Create link for ₹{price}
      </button>
    </form>
  );
};

export const PaymentLinkDialog = ({ open, link, onClose, onChange }) => {
  const [made, setMade] = useState(null);
  useEffect(() => { if (open) setMade(link || null); }, [open, link]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md bg-white border-slate-200" data-testid="payment-link-dialog">
        <DialogTitle className="text-slate-900">{made ? "Scan or share to pay" : "Request a payment"}</DialogTitle>
        <DialogDescription className="text-sm text-slate-500">{made ? "Keep this open — it updates as soon as the merchant pays." : "Create a real Razorpay link (UPI, card, netbanking) for a merchant."}</DialogDescription>
        {made ? <LinkResult link={made} onChange={onChange} /> : <LinkForm onCreated={(l) => { setMade(l); onChange?.(); }} />}
      </DialogContent>
    </Dialog>
  );
};

export default PaymentLinkDialog;
